import { useState } from 'react';
import { ChevronUp, ChevronDown, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

interface UploadTask {
  id: string;
  filename: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
}

interface UploadStatusProps {
  uploads: UploadTask[];
  onRemoveUpload: (id: string) => void;
}

export default function UploadStatus({ uploads, onRemoveUpload }: UploadStatusProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (uploads.length === 0) return null;

  const activeUploads = uploads.filter(u => u.status === 'uploading' || u.status === 'processing');
  // const completedUploads = uploads.filter(u => u.status === 'completed');
  const errorUploads = uploads.filter(u => u.status === 'error');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploading': return 'Uploading...';
      case 'processing': return 'Processing...';
      case 'completed': return 'Completed';
      case 'error': return 'Failed';
      default: return status;
    }
  };

  return (
    <div className="fixed bottom-4 right-4 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-40" style={{ marginLeft: '256px' }}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 border-b border-gray-200 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            {activeUploads.length > 0 && (
              <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            )}
            <span className="text-sm font-medium text-gray-900">
              Upload Tasks ({uploads.length})
            </span>
          </div>
          {activeUploads.length > 0 && (
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
              {activeUploads.length} active
            </span>
          )}
          {errorUploads.length > 0 && (
            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
              {errorUploads.length} failed
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="max-h-64 overflow-y-auto">
          {uploads.map((upload) => (
            <div key={upload.id} className="p-3 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {getStatusIcon(upload.status)}
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {upload.filename}
                  </span>
                </div>
                <button
                  onClick={() => onRemoveUpload(upload.id)}
                  className="text-gray-400 hover:text-gray-600 ml-2"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{getStatusText(upload.status)}</span>
                {upload.status === 'uploading' && (
                  <span>{Math.round(upload.progress)}%</span>
                )}
              </div>

              {(upload.status === 'uploading' || upload.status === 'processing') && (
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}

              {upload.error && (
                <p className="text-xs text-red-600 mt-1">{upload.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick summary when collapsed */}
      {!isExpanded && activeUploads.length > 0 && (
        <div className="p-2">
          <div className="text-xs text-gray-600">
            {activeUploads[0].filename}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
            <div
              className="bg-blue-600 h-1 rounded-full transition-all duration-300"
              style={{ width: `${activeUploads[0].progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}